import React, { useEffect, useState } from 'react';
import { path, prop } from 'ramda';
import { dispatcher } from 'dispatcher';
import * as urlUtil from '@core/js/utils/url.util';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';
import { Icon } from '@core/js/components/common/Icon';
import { BackboneReplacer } from '@core/js/backbone/components/BackboneReplacer';
import { AccessibilityElement } from '@core/js/components/common/AccessibilityElement';
import { useComponentTranslations } from '@core/js/hooks/component/useComponentTranslations';

import * as Types from './../../types';
import * as helpers from '@theme/js/components/main/NavigationBar/helpers';
import * as hooks from '@core/js/components/main/DefaultHeader/hooks';

import style from './Inbox.scss';

export const Inbox: React.FC = () => {
    const translate = useComponentTranslations();
    const inboxWidget = useAppSelector(prop('inboxWidget'));
    const onInboxButtonClick = hooks.useOnInboxButtonClick();
    const hideInboxByUrl = urlUtil.getLocationParam('hideInbox') === '1';
    /* TotalCasino changes */
    const contactPreferences = useUserSelector(path(['userDetails', 'contactPreferences']));
    const canShowInbox = (
        widget: { showIcon: boolean; enabled: boolean },
        hideByUrl: boolean,
        preferences: Types.TContactPreferences
    ): boolean => {
        return widget.showIcon && widget.enabled && !hideByUrl && helpers.getShowInboxIcon(preferences);
    };
    const [showInbox, setShowInbox] = useState(() => canShowInbox(
        inboxWidget, hideInboxByUrl, contactPreferences as Types.TContactPreferences
    ));

    useEffect(() => {
        const updateInboxVisibility = (data: Types.TContactPreferences) => {
            const existingKeys = new Set(data.map(p => `${p.channel}:${p.type}`));
            const preferences = new Set([
                ...data,
                ...(contactPreferences as Types.TContactPreferences || [])
                    .filter(pref => !existingKeys.has(`${pref.channel}:${pref.type}`))
            ]);

            setShowInbox(
                canShowInbox(
                    inboxWidget,
                    hideInboxByUrl,
                    [...preferences]
                )
            );
        };

        dispatcher.listen('user:details:update', updateInboxVisibility);
        dispatcher.listen('user:toggleInboxInNavigation', updateInboxVisibility);

        return () => {
            dispatcher.removeListener('user:details:update', updateInboxVisibility);
            dispatcher.removeListener('user:toggleInboxInNavigation', updateInboxVisibility);
        };
    }, [contactPreferences, hideInboxByUrl, inboxWidget]);
    /* TotalCasino changes */

    return showInbox
        ? (
            <AccessibilityElement
                tagName="span"
                className={style.inbox}
                onClick={onInboxButtonClick}
                aria-label={translate('header.inboxBtn')}
            >
                <Icon type="headerInboxIcon" className={`${style.icon} cms-icon-inbox`} />
                <BackboneReplacer
                    viewName="unreadmessagescount"
                    dynamicName="UNREAD_MESSAGES_COUNT"
                />
            </AccessibilityElement>
        )
        : null;
};
